import { NEUTRAL } from "../../../../theme/tokens";
import { DETAIL_ROW_CLASS } from "./utils";

export interface FrozenColumnsColors {
	/** Must be opaque — the scrolling header cells slide underneath it. */
	headerBackground: string;
	/** Must be opaque — the scrolling body cells slide underneath it. */
	cellBackground: string;
	cellHoverBackground: string;
}

/** Marks the boundary between the frozen zone and the scrolling columns. */
const FREEZE_DIVIDER = `2px solid ${NEUTRAL[300]}`;

// MUI's own pinned-column layers: headers must clear the column separators (z-index 30), which are
// absolutely positioned and otherwise draw — and take resize drags — across the frozen headers.
const FROZEN_CELL_Z_INDEX = 30;
const FROZEN_HEADER_Z_INDEX = 40;
const FROZEN_BAND_Z_INDEX = 41;

/**
 * Sticky-positioning rules that freeze a grid's leading columns on horizontal scroll (MUI's community
 * DataGrid has no column pinning). Layer the result onto `SystemDataGrid`'s `sx`.
 *
 * `fields` are the frozen columns in display order, visible ones only — they must be the grid's first
 * columns. A sticky column sticks at the summed width of every frozen column before it, so `widths` must
 * hold a fixed px width for each of those (keep them `resizable: false`); the last one may flex. The last
 * column carries the divider. Offsets are inline-start, so a right-to-left page freezes them on the right.
 *
 * The grid must also pass `disableVirtualization`, or column virtualization unmounts the frozen cells
 * once the grid is scrolled far enough right. Detail rows are excluded: their single cell col-spans the
 * full width to host the expanded panel, so freezing it would float the panel during horizontal scroll.
 *
 * The frozen columns must be ungrouped when the grid has `headerGroups`: MUI renders one blank band cell
 * over them, which is frozen here too so the band row doesn't scroll away above the frozen leaf headers.
 */
export const frozenColumnsSx = (
	fields: readonly string[],
	widths: Readonly<Partial<Record<string, number>>>,
	colors: FrozenColumnsColors,
): Record<string, Record<string, string | number>> => {
	const sx: Record<string, Record<string, string | number>> = {};
	let offset = 0;
	fields.forEach((field, index) => {
		const isLast = index === fields.length - 1;
		const divider: Record<string, string> = isLast ? { borderInlineEnd: FREEZE_DIVIDER } : {};
		sx[`& .MuiDataGrid-columnHeader[data-field="${field}"]`] = {
			position: "sticky",
			insetInlineStart: offset,
			zIndex: FROZEN_HEADER_Z_INDEX,
			backgroundColor: colors.headerBackground,
			...divider,
		};
		sx[`& .MuiDataGrid-row:not(.${DETAIL_ROW_CLASS}) .MuiDataGrid-cell[data-field="${field}"]`] = {
			position: "sticky",
			insetInlineStart: offset,
			zIndex: FROZEN_CELL_Z_INDEX,
			backgroundColor: colors.cellBackground,
			...divider,
		};
		sx[`& .MuiDataGrid-row:not(.${DETAIL_ROW_CLASS}):hover .MuiDataGrid-cell[data-field="${field}"]`] = {
			backgroundColor: colors.cellHoverBackground,
		};
		if (isLast) return;
		const width = widths[field];
		if (width === undefined) throw new Error(`frozenColumnsSx: no fixed width for frozen column "${field}"`);
		offset += width;
	});
	// Band cells carry `data-fields` (plural), which a leaf header's `data-field` never matches.
	if (fields.length > 0) {
		sx[`& .MuiDataGrid-columnHeader[data-fields*="|-${fields[0]}-|"]`] = {
			position: "sticky",
			insetInlineStart: 0,
			zIndex: FROZEN_BAND_Z_INDEX,
			backgroundColor: colors.headerBackground,
			borderInlineEnd: FREEZE_DIVIDER,
		};
	}
	return sx;
};
